/**
 * Camera — follows the player horizontally (brief), clamped to level bounds.
 * Vertical is clamped too so we never show past the top/bottom edge; it eases
 * toward the player so tall jumps still read without jarring snaps.
 *
 * POLISH_01 #3: X now EASES (not a hard center-lock) and LEADS in the travel direction so the
 * four high-speed archetypes (momentum/run-and-gun/auto-runner/dash) get reaction room ahead of
 * the player instead of sitting dead-center. Render-only (World-owned; the sim never reads the
 * camera) → replay parity is unaffected. A snap caller (respawn/warp) passes ease=1 to bypass
 * both the X ease and the lead for an instant cut.
 */
export class Camera {
  x = 0;
  y = 0;

  /** Per-frame X ease toward the (lead-adjusted) target when NOT snapping. */
  private static readonly X_EASE = 0.14;
  /** Max lookahead lead (px) at full speed. */
  private static readonly LOOKAHEAD_MAX = 90;
  /** Lead px per px/s of |vx| (scaled, then capped at LOOKAHEAD_MAX). */
  private static readonly LOOKAHEAD_SCALE = 0.18;

  constructor(
    private viewW: number,
    private viewH: number,
    private worldW: number,
    private worldH: number,
  ) {}

  resize(viewW: number, viewH: number): void {
    this.viewW = viewW;
    this.viewH = viewH;
  }

  setWorld(worldW: number, worldH: number): void {
    this.worldW = worldW;
    this.worldH = worldH;
  }

  /**
   * Center on a target point, clamp to bounds. `ease` in [0..1] controls BOTH axes' easing;
   * ease = 1 snaps (used on respawn/warp). `vx`/`facing` drive the horizontal lookahead lead —
   * both default to 0 (no lead) so snap callers and legacy call sites are unaffected.
   */
  follow(targetX: number, targetY: number, ease = 0.15, vx = 0, facing = 0): void {
    // Lead the camera in the travel direction, scaled by speed and capped. Snap callers pass
    // facing/vx = 0 (default) so a snap never leads.
    const speedLead = Math.min(Camera.LOOKAHEAD_MAX, Math.abs(vx) * Camera.LOOKAHEAD_SCALE);
    const lead = facing * speedLead;
    const desiredX = clamp((targetX + lead) - this.viewW / 2, 0, Math.max(0, this.worldW - this.viewW));
    if (ease >= 1) this.x = desiredX;                       // instant cut (respawn / warp)
    else this.x += (desiredX - this.x) * Camera.X_EASE;     // smooth follow + lead

    const desiredY = clamp(targetY - this.viewH / 2, 0, Math.max(0, this.worldH - this.viewH));
    this.y += (desiredY - this.y) * ease;
  }

  snap(): void { this.y = clamp(this.y, 0, Math.max(0, this.worldH - this.viewH)); }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
